/**
 * VIDEO tab for settings panel.
 * Post-processing effect controls under a collapsible header.
 * Uses Imperial Forge styling via ForgeControls components.
 */

import { useCallback } from 'react';
import { usePostprocessingStore, ToneMappingMode } from '../../../stores/postprocessingStore';
import type { ToneMappingModeValue } from '../../../stores/postprocessingStore';
import { EffectSection, CollapsibleSection, Slider, ForgeToggle, ForgeSelect } from './ForgeControls';
import styles from './DesignTab.module.css';

// ---- Tone mapping mode options ----

const TONE_MAPPING_OPTIONS = [
  { label: 'Linear', value: String(ToneMappingMode.LINEAR) },
  { label: 'Reinhard', value: String(ToneMappingMode.REINHARD) },
  { label: 'Uncharted 2', value: String(ToneMappingMode.UNCHARTED2) },
  { label: 'ACES Filmic', value: String(ToneMappingMode.ACES_FILMIC) },
];

// ---- Main component ----

export function VideoTab() {
  const settings = usePostprocessingStore((s) => s.settings);
  const updateBloom = usePostprocessingStore((s) => s.updateBloom);
  const updateVignette = usePostprocessingStore((s) => s.updateVignette);
  const updateChromaticAberration = usePostprocessingStore((s) => s.updateChromaticAberration);
  const updateNoise = usePostprocessingStore((s) => s.updateNoise);
  const updateToneMapping = usePostprocessingStore((s) => s.updateToneMapping);
  const updateGlitch = usePostprocessingStore((s) => s.updateGlitch);
  const updateScanline = usePostprocessingStore((s) => s.updateScanline);
  const updateSmaa = usePostprocessingStore((s) => s.updateSmaa);
  const resetBloom = usePostprocessingStore((s) => s.resetBloom);
  const resetVignette = usePostprocessingStore((s) => s.resetVignette);
  const resetChromaticAberration = usePostprocessingStore((s) => s.resetChromaticAberration);
  const resetNoise = usePostprocessingStore((s) => s.resetNoise);
  const resetToneMapping = usePostprocessingStore((s) => s.resetToneMapping);
  const resetGlitch = usePostprocessingStore((s) => s.resetGlitch);
  const resetScanline = usePostprocessingStore((s) => s.resetScanline);
  const resetSmaa = usePostprocessingStore((s) => s.resetSmaa);
  const resetToDefaults = usePostprocessingStore((s) => s.resetToDefaults);

  const handleReset = useCallback(() => {
    resetToDefaults();
  }, [resetToDefaults]);

  return (
    <div className={styles.container}>
      <CollapsibleSection name="Post Processing" defaultExpanded={false}>
        {/* Bloom */}
        <EffectSection name="Bloom" enabled={settings.bloom.enabled} onToggle={(v) => updateBloom({ enabled: v })} onReset={resetBloom}>
          <Slider label="Intensity" value={settings.bloom.intensity} min={0} max={20} step={0.1} onChange={(v) => updateBloom({ intensity: v })} />
          <Slider label="Radius" value={settings.bloom.radius} min={0} max={1} step={0.01} onChange={(v) => updateBloom({ radius: v })} />
          <Slider label="Lum. Threshold" value={settings.bloom.luminanceThreshold} min={0} max={1} step={0.01} onChange={(v) => updateBloom({ luminanceThreshold: v })} />
          <Slider label="Lum. Smoothing" value={settings.bloom.luminanceSmoothing} min={0} max={1} step={0.01} onChange={(v) => updateBloom({ luminanceSmoothing: v })} />
          <ForgeToggle label="Mipmap Blur" checked={settings.bloom.mipmapBlur} onChange={(v) => updateBloom({ mipmapBlur: v })} />
        </EffectSection>

        {/* Vignette */}
        <EffectSection name="Vignette" enabled={settings.vignette.enabled} onToggle={(v) => updateVignette({ enabled: v })} onReset={resetVignette}>
          <Slider label="Darkness" value={settings.vignette.darkness} min={0} max={1} step={0.01} onChange={(v) => updateVignette({ darkness: v })} />
          <Slider label="Offset" value={settings.vignette.offset} min={0} max={1} step={0.01} onChange={(v) => updateVignette({ offset: v })} />
        </EffectSection>

        {/* Chromatic Aberration */}
        <EffectSection name="Chromatic Aberration" enabled={settings.chromaticAberration.enabled} onToggle={(v) => updateChromaticAberration({ enabled: v })} onReset={resetChromaticAberration}>
          <Slider label="Offset X" value={settings.chromaticAberration.offsetX} min={0} max={0.1} step={0.001} decimals={3} onChange={(v) => updateChromaticAberration({ offsetX: v })} />
          <Slider label="Offset Y" value={settings.chromaticAberration.offsetY} min={0} max={0.1} step={0.001} decimals={3} onChange={(v) => updateChromaticAberration({ offsetY: v })} />
          <ForgeToggle label="Radial Modulation" checked={settings.chromaticAberration.radialModulation} onChange={(v) => updateChromaticAberration({ radialModulation: v })} />
          <Slider label="Mod. Offset" value={settings.chromaticAberration.modulationOffset} min={0} max={1} step={0.01} onChange={(v) => updateChromaticAberration({ modulationOffset: v })} />
        </EffectSection>

        {/* Noise */}
        <EffectSection name="Noise" enabled={settings.noise.enabled} onToggle={(v) => updateNoise({ enabled: v })} onReset={resetNoise}>
          <Slider label="Opacity" value={settings.noise.opacity} min={0} max={1} step={0.01} onChange={(v) => updateNoise({ opacity: v })} />
          <ForgeToggle label="Premultiply" checked={settings.noise.premultiply} onChange={(v) => updateNoise({ premultiply: v })} />
        </EffectSection>

        {/* Tone Mapping */}
        <EffectSection name="Tone Mapping" enabled={settings.toneMapping.enabled} onToggle={(v) => updateToneMapping({ enabled: v })} onReset={resetToneMapping}>
          <ForgeSelect
            label="Mode"
            value={String(settings.toneMapping.mode)}
            options={TONE_MAPPING_OPTIONS}
            onChange={(v) => updateToneMapping({ mode: Number(v) as ToneMappingModeValue })}
          />
        </EffectSection>

        {/* Glitch */}
        <EffectSection name="Glitch" enabled={settings.glitch.enabled} onToggle={(v) => updateGlitch({ enabled: v })} onReset={resetGlitch}>
          <Slider label="Delay Min" value={settings.glitch.delayMin} min={0} max={60} step={0.1} decimals={1} onChange={(v) => updateGlitch({ delayMin: v })} />
          <Slider label="Delay Max" value={settings.glitch.delayMax} min={0} max={60} step={0.1} decimals={1} onChange={(v) => updateGlitch({ delayMax: v })} />
          <Slider label="Duration Min" value={settings.glitch.durationMin} min={0} max={10} step={0.01} onChange={(v) => updateGlitch({ durationMin: v })} />
          <Slider label="Duration Max" value={settings.glitch.durationMax} min={0} max={10} step={0.01} onChange={(v) => updateGlitch({ durationMax: v })} />
          <Slider label="Strength Min" value={settings.glitch.strengthMin} min={0} max={2} step={0.01} onChange={(v) => updateGlitch({ strengthMin: v })} />
          <Slider label="Strength Max" value={settings.glitch.strengthMax} min={0} max={2} step={0.01} onChange={(v) => updateGlitch({ strengthMax: v })} />
        </EffectSection>

        {/* Scanline */}
        <EffectSection name="Scanline" enabled={settings.scanline.enabled} onToggle={(v) => updateScanline({ enabled: v })} onReset={resetScanline}>
          <Slider label="Density" value={settings.scanline.density} min={0} max={20} step={0.1} decimals={1} onChange={(v) => updateScanline({ density: v })} />
          <Slider label="Opacity" value={settings.scanline.opacity} min={0} max={1} step={0.01} onChange={(v) => updateScanline({ opacity: v })} />
          <Slider label="Scroll Speed" value={settings.scanline.scrollSpeed} min={0} max={5} step={0.1} decimals={1} onChange={(v) => updateScanline({ scrollSpeed: v })} />
        </EffectSection>

        {/* SMAA */}
        <EffectSection name="SMAA" enabled={settings.smaa.enabled} onToggle={(v) => updateSmaa({ enabled: v })} onReset={resetSmaa}>
          {/* SMAA has no parameters — toggle only */}
        </EffectSection>

        {/* Reset Defaults */}
        <div className={styles.actions}>
          <button type="button" className={styles.actionButton} onClick={handleReset}>
            Reset Defaults
          </button>
        </div>
      </CollapsibleSection>
    </div>
  );
}
